// THE NETWORK VOCABULARY, VERSION 1 (BUILD-BRIEF-R5 §1) — who works with whom: publics vs
// regimes (§1.1/§1.2), the roles/bases/qualifiers/residuals of a link (§1.3), and the row and
// response shapes every other package types against (§1.4).
//
// ZERO IMPORTS. This is load-bearing (BUILD-BRIEF-R5, "module-cycle decision"): `raster.v1.ts`
// imports `REGIME_VALUES` from this file, so this file must never import anything from
// `raster.v1.ts` — a cycle there deadlocks at module init (a TDZ crash on `REGIME_VALUES` inside
// `buildBlock`). The qualifier primitives the brief describes as living in `raster.v1.ts` are
// therefore DECLARED HERE and re-exported from there in one line, so a caller that reaches for
// them by the brief's name still finds them.
//
// Labels come out of `linkLabel(lang, kind, token)` — like raster labels, these are NOT i18n
// catalogue keys.

export type NetLang = 'en' | 'de' | 'fr';

// ── the numeric qualifier (adopted here; raster.v1.ts re-exports it) ────────────────────────

export type NumericQualifier = 'at_least' | 'at_most' | 'approx';
export const NUMERIC_QUALIFIERS: readonly NumericQualifier[] = ['at_least', 'at_most', 'approx'];
export const QUALIFIER_SIGN: Record<NumericQualifier, string> = {
  at_least: '≥',
  at_most: '≤',
  approx: '≈',
};
export type LinkQualifier = NumericQualifier;

// The one prefix table (the branch's, 2026-09-05 — three languages and the signs; longest
// alternative first: "mindestens" must not be read as "min" + "destens"). `à partir de` and
// `près de` are round 5's two additions.
const QUALIFIER_PREFIXES: Array<[RegExp, NumericQualifier]> = [
  [/^(?:>=|≥|=>|at\s+least|au\s+moins|au\s+minimum|à\s+partir\s+de|a\s+partir\s+de|mindestens|minimum|mind\.?|min\.?|plus\s+de|more\s+than|over|dès)\s*/i, 'at_least'],
  [/^(?:<=|≤|=<|at\s+most|au\s+plus|au\s+maximum|maximum|max\.?|höchstens|hochstens|up\s+to|jusqu['’]?à|moins\s+de|less\s+than|under)\s*/i, 'at_most'],
  [/^(?:~|≈|approximately|approximativement|approx\.?|environ|env\.?|about|around|circa|ca\.?|rund|etwa|près\s+de|pres\s+de|quelques?|some)\s*/i, 'approx'],
];

/**
 * Split "≥116", "116+", "environ 250", "~ 250" into the qualifier and the bare figure. A plain
 * number comes back unqualified and unchanged. `'116+'` is the one SUFFIX form; every other
 * qualifier is a PREFIX. Exported: the canonicaliser, the propose tool and `parseQuantity` read
 * a value through this before they parse it.
 */
export function splitQualifier(raw: string): { rest: string; qualifier: NumericQualifier | null } {
  let s = String(raw ?? '').trim();
  let qualifier: NumericQualifier | null = null;
  for (const [re, q] of QUALIFIER_PREFIXES) {
    if (re.test(s)) { qualifier = q; s = s.replace(re, '').trim(); break; }
  }
  // "116+" / "116 +" — a trailing plus is a floor.
  if (!qualifier && /\+\s*$/.test(s)) { qualifier = 'at_least'; s = s.replace(/\+\s*$/, '').trim(); }
  return { rest: s, qualifier };
}

// ── regimes (§1.2) ────────────────────────────────────────────────────────────────────────────

export type RegimeToken =
  | 'ri' | 'ai' | 'laci' | 'asylum' | 'refugee_integration' | 'adult_basic_education'
  | 'post_compulsory' | 'child_youth_protection' | 'cantonal_disability' | 'health' | 'justice'
  | 'schools' | 'private' | 'other';

export const REGIME_TOKENS: readonly RegimeToken[] = [
  'ri', 'ai', 'laci', 'asylum', 'refugee_integration', 'adult_basic_education',
  'post_compulsory', 'child_youth_protection', 'cantonal_disability', 'health', 'justice',
  'schools', 'private', 'other',
];

export type RegimeValueSpec = readonly [value: string, en: string, de: string, fr: string];

export const REGIME_VALUES: readonly RegimeValueSpec[] = [
  ['ri', 'Social aid (RI)', 'Sozialhilfe (RI)', "Revenu d'insertion (RI)"],
  ['ai', 'Disability insurance (AI)', 'Invalidenversicherung (IV)', 'Assurance-invalidité (AI)'],
  ['laci', 'Unemployment insurance (LACI)', 'Arbeitslosenversicherung (AVIG)', 'Assurance-chômage (LACI)'],
  ['asylum', 'Asylum (EVAM)', 'Asyl (EVAM)', 'Asile (EVAM)'],
  ['refugee_integration', 'Refugee integration (CSIR · BCI)', 'Flüchtlingsintegration (CSIR · BCI)', 'Intégration des réfugiés (CSIR · BCI)'],
  ['adult_basic_education', 'Adult basic education (CIFÉA)', 'Grundbildung Erwachsene (CIFÉA)', 'Formation de base des adultes (CIFÉA)'],
  ['post_compulsory', 'Post-compulsory education (DGEP)', 'Nachobligatorische Bildung (DGEP)', 'Enseignement postobligatoire (DGEP)'],
  ['child_youth_protection', 'Child and youth protection (DGEJ)', 'Kinder- und Jugendschutz (DGEJ)', "Protection de l'enfance et de la jeunesse (DGEJ)"],
  ['cantonal_disability', 'Cantonal disability mandate (LAIH)', 'Kantonaler Behindertenauftrag', 'Mandat cantonal handicap (LAIH)'],
  ['health', 'Health (hospitals · CMS)', 'Gesundheit (Spitäler · CMS)', 'Santé (hôpitaux · CMS)'],
  ['justice', 'Justice (courts · probation)', 'Justiz (Gerichte · Bewährungshilfe)', 'Justice (tribunaux · probation)'],
  ['schools', 'Schools', 'Schulen', 'Écoles'],
  ['private', 'Private clients and employers', 'Private und Arbeitgeber', 'Privés et employeurs'],
  ['other', 'Other', 'Andere', 'Autre'],
];

// ── roles, bases, residuals (§1.3) ───────────────────────────────────────────────────────────

export type LinkRole =
  | 'payer' | 'referrer' | 'funder' | 'umbrella' | 'partner' | 'accreditor' | 'vendor'
  | 'consultant' | 'auditor';
export const LINK_ROLES: readonly LinkRole[] = [
  'payer', 'referrer', 'funder', 'umbrella', 'partner', 'accreditor', 'vendor', 'consultant', 'auditor',
];

export type LinkBasis = 'participants_pct' | 'participants_count' | 'revenue_pct' | 'amount_chf' | 'places_count';
export const LINK_BASES: readonly LinkBasis[] = [
  'participants_pct', 'participants_count', 'revenue_pct', 'amount_chf', 'places_count',
];

export type LinkResidual = 'other_mandates' | 'general_public' | 'unspecified';
export const LINK_RESIDUALS: readonly LinkResidual[] = ['other_mandates', 'general_public', 'unspecified'];

/**
 * A percentage is shown per row and is never summed across organisations; counts and amounts are
 * summed within one year only — a Σ CHF column always names its year.
 */
export const SUMMABLE_BASES: readonly LinkBasis[] = ['participants_count', 'places_count', 'amount_chf'];

/** Mirrors the DDL's `links_quantity_role` — the roles a quantity may be attached to. */
export const QUANTITY_ROLES: readonly LinkRole[] = ['payer', 'referrer', 'funder', 'accreditor', 'vendor', 'consultant'];

/** A residual distribution line is allowed only on these two roles. */
export const RESIDUAL_ROLES: readonly LinkRole[] = ['payer', 'referrer'];

export const BASIS_UNIT: Record<LinkBasis, string> = {
  participants_pct: '%',
  participants_count: 'persons',
  revenue_pct: '%',
  amount_chf: 'CHF',
  places_count: 'places',
};

// ── quantity parsing (§1.4) ───────────────────────────────────────────────────────────────────

const MILLION_RE = /\b(mio\.?s?|millions?)\b/i;
const THOUSAND_RE = /\b(milliers?|tsd)\b/i;
const BARE_M_RE = /\bm\b/i;
const BARE_K_RE = /\bk\b/i;
const K_CHF_RE = /\bk(?=chf)/i;

function detectMultiplier(raw: string): number {
  if (MILLION_RE.test(raw) || BARE_M_RE.test(raw)) return 1_000_000;
  if (THOUSAND_RE.test(raw) || BARE_K_RE.test(raw) || K_CHF_RE.test(raw)) return 1_000;
  return 1;
}

/** Strips thousands separators (never the decimal comma/dot) and reads the figure as written. */
function parseFigure(s: string): { value: number; digits: string } | null {
  const cleaned = s.replace(new RegExp('[\x27\u2019\x60\x20\u00A0\u202F]', 'g'), '');
  const m = cleaned.match(/-?\d+(?:[.,]\d+)?/);
  if (!m) return null;
  const raw = m[0];
  let value = Number(raw.replace(',', '.'));
  if (!Number.isFinite(value)) return null;
  const digits = raw.replace(/[^\d]/g, '');
  value *= detectMultiplier(s);
  return { value, digits };
}

/**
 * Splits `raw` with `splitQualifier` first, then reads the figure as the source writes it —
 * thousands separators `'` `’` `` ` `` and space, decimal comma or dot, a trailing `.–`, and the
 * multipliers `k | milliers | tsd` (×1000) and `mio | millions | m` (×1'000'000), mirroring
 * `aliases.ts#toNumber`. `digits` is the digit string of the raw figure BEFORE any multiplier and
 * with every separator removed — what §3.2 step 8 looks for in the quote, because the quote
 * carries the source's spelling, not the parsed value.
 */
export function parseQuantity(
  basis: LinkBasis,
  raw: number | string,
): { value: number; qualifier: LinkQualifier | null; digits: string } | { error: string } {
  const input = typeof raw === 'number' ? String(raw) : raw;
  const { rest, qualifier } = splitQualifier(input);
  const figure = parseFigure(rest);
  if (!figure) return { error: `${basis} is a number of ${BASIS_UNIT[basis]}` };
  const { value, digits } = figure;
  if (value < 0) return { error: `${basis} cannot be negative` };
  if ((basis === 'participants_pct' || basis === 'revenue_pct') && value > 100) {
    return { error: `a ${basis} is a share 0–100, not ${value}` };
  }
  return { value, qualifier, digits };
}

const QUANTITY_UNIT_WORD: Record<'participants_count' | 'places_count', Record<NetLang, string>> = {
  participants_count: { en: 'participants', de: 'Teilnehmende', fr: 'participants' },
  places_count: { en: 'places', de: 'Plätze', fr: 'places' },
};

function fmtMoneyNarrow(n: number): string {
  const neg = n < 0;
  const abs = Math.round(Math.abs(n));
  const s = String(abs).replace(/\B(?=(\d{3})+(?!\d))/g, '’');
  return (neg ? '-' : '') + s;
}

/**
 * `"57 %"`, `"≥ 116 places"`, `"CHF 80’000"`, `"≈ 250 participants"`. The ONLY sign this may ever
 * print is `QUALIFIER_SIGN[qualifier]` — with no qualifier there is no sign, ever.
 */
export function quantityText(lang: NetLang, basis: LinkBasis, quantity: number, qualifier?: LinkQualifier | null): string {
  const sign = qualifier ? `${QUALIFIER_SIGN[qualifier]} ` : '';
  switch (basis) {
    case 'participants_pct':
    case 'revenue_pct':
      return `${sign}${quantity} %`;
    case 'amount_chf':
      return `CHF ${sign}${fmtMoneyNarrow(quantity)}`;
    case 'participants_count':
    case 'places_count':
      return `${sign}${quantity} ${QUANTITY_UNIT_WORD[basis][lang]}`;
  }
}

// ── labels (§1.4) — NOT i18n catalogue keys, exactly as raster labels are not ──────────────────

type Labelled = Record<NetLang, string>;

const ROLE_LABELS: Record<LinkRole, { role: Labelled; reverse: Labelled }> = {
  payer: { role: { en: 'payer', de: 'Kostenträger', fr: 'financeur' }, reverse: { en: 'pays for', de: 'finanziert', fr: 'finance' } },
  referrer: { role: { en: 'referrer', de: 'Zuweiser', fr: 'prescripteur' }, reverse: { en: 'refers to', de: 'weist zu', fr: 'prescrit à' } },
  funder: { role: { en: 'funder', de: 'Geldgeber', fr: 'bailleur' }, reverse: { en: 'funds', de: 'subventioniert', fr: 'subventionne' } },
  umbrella: { role: { en: 'umbrella', de: 'Dachverband', fr: 'faîtière' }, reverse: { en: 'has member', de: 'Mitglied', fr: 'a pour membre' } },
  partner: { role: { en: 'partner', de: 'Partner', fr: 'partenaire' }, reverse: { en: 'partner', de: 'Partner', fr: 'partenaire' } },
  accreditor: { role: { en: 'accreditor', de: 'Bewilligungsbehörde', fr: 'autorité de reconnaissance' }, reverse: { en: 'accredits', de: 'bewilligt', fr: 'reconnaît' } },
  vendor: { role: { en: 'vendor', de: 'Lieferant', fr: 'fournisseur' }, reverse: { en: 'supplies', de: 'liefert', fr: 'fournit' } },
  consultant: { role: { en: 'consultant', de: 'Berater', fr: 'conseil' }, reverse: { en: 'advises', de: 'berät', fr: 'conseille' } },
  auditor: { role: { en: 'auditor', de: 'Revisionsstelle', fr: 'organe de révision' }, reverse: { en: 'audits', de: 'prüft', fr: 'révise' } },
};

const BASIS_LABELS: Record<LinkBasis, Labelled> = {
  participants_pct: { en: 'Share of participants', de: 'Anteil Teilnehmende', fr: 'Part des participants' },
  participants_count: { en: 'Participants', de: 'Teilnehmende', fr: 'Participants' },
  revenue_pct: { en: 'Share of revenue', de: 'Anteil Ertrag', fr: 'Part des produits' },
  amount_chf: { en: 'Amount (CHF)', de: 'Betrag (CHF)', fr: 'Montant (CHF)' },
  places_count: { en: 'Places', de: 'Plätze', fr: 'Places' },
};

const RESIDUAL_LABELS: Record<LinkResidual, Labelled> = {
  other_mandates: { en: 'Other mandates', de: 'Andere Mandate', fr: 'Autres mandats' },
  general_public: { en: 'General public', de: 'Allgemeine Bevölkerung', fr: 'Grand public' },
  unspecified: { en: 'Unspecified', de: 'Nicht spezifiziert', fr: 'Non précisé' },
};

/** Three-language tables from §1.2/§1.3 — NOT i18n catalogue keys. An unknown token is itself. */
export function linkLabel(lang: NetLang, kind: 'role' | 'reverse' | 'basis' | 'regime' | 'residual', token: string): string {
  switch (kind) {
    case 'role': return ROLE_LABELS[token as LinkRole]?.role[lang] ?? token;
    case 'reverse': return ROLE_LABELS[token as LinkRole]?.reverse[lang] ?? token;
    case 'basis': return BASIS_LABELS[token as LinkBasis]?.[lang] ?? token;
    case 'regime': {
      const spec = REGIME_VALUES.find((r) => r[0] === token);
      if (!spec) return token;
      return lang === 'en' ? spec[1] : lang === 'de' ? spec[2] : spec[3];
    }
    case 'residual': return RESIDUAL_LABELS[token as LinkResidual]?.[lang] ?? token;
  }
}

/** The current-one key as a string, for tests and Review. `null` segments are empty. */
export function slotOf(row: Pick<LinkRow, 'subject_id' | 'role' | 'counterpart_id' | 'regime' | 'residual' | 'basis' | 'year' | 'label'>): string {
  const parts: Array<string | number | null | undefined> = [
    row.subject_id, row.role, row.counterpart_id, row.regime, row.residual, row.basis, row.year, row.label,
  ];
  return parts.map((p) => (p === null || p === undefined ? '' : String(p))).join('|');
}

// ── the row and response shapes (§1.4) ───────────────────────────────────────────────────────

export interface LinkRow {
  id: number;
  subject_id: number;
  subject_ref: string | null;
  subject_name: string | null;
  counterpart_id: number | null;
  counterpart_ref: string | null;
  counterpart_name: string | null;
  regime: RegimeToken | null;
  residual: LinkResidual | null;
  role: LinkRole;
  basis: LinkBasis | null;
  quantity: number | null;
  qualifier: LinkQualifier | null;
  year: number | null;
  label: string | null;
  as_of: string | null;
  ended_on: string | null;
  status: 'current' | 'superseded' | 'retracted';
  superseded_by: number | null;
  retract_note: string | null;
  method: 'read' | 'estimated' | 'declared' | 'not_found';
  confidence: number | null;
  evidence_kind: 'url' | 'snapshot' | 'interaction' | 'document' | 'manual' | null;
  evidence_ref: string | null;
  quote: string | null;
  proposal_id: number | null;
  proposal_ref: string | null;
  principal: string | null;
  confirmed_by: number | null;
  confirmed_at: string | null;
  created_at: string;
  created_by_kind: 'operator' | 'agent' | 'import';
}

export interface LinkGhost {
  proposal_id: number;
  ref: string;
  role: LinkRole;
  counterpart_id: number | null;
  counterpart_ref: string | null;
  counterpart_name: string | null;
  regime: RegimeToken | null;
  residual: LinkResidual | null;
  basis: LinkBasis | null;
  quantity: number | null;
  qualifier: LinkQualifier | null;
  year: number | null;
  label: string | null;
}

export interface MixRow {
  link_id: number;
  counterpart_id: number | null;
  counterpart_ref: string | null;
  counterpart_name: string | null;
  regime: RegimeToken | null;
  residual: LinkResidual | null;
  quantity: number;
  qualifier: LinkQualifier | null;
  share_computed: number | null;
  share_title: string | null;
  method: LinkRow['method'];
  held: boolean;
  held_by_rule: boolean;
  quote: string | null;
  evidence_kind: LinkRow['evidence_kind'];
  evidence_ref: string | null;
  as_of: string | null;
}

export interface LinkMix {
  basis: LinkBasis;
  year: number | null;
  rows: MixRow[];
  attributed_pct: number | null;
}

/** A `named_by` row (Sheet C) carries §6.1's beneficiaries join beside the share the payer's own
 *  page shows: `participants_pct × clients_per_year`, rounded, computed by the CRM — so it is
 *  shown with no claim mark, and `beneficiaries_title` says the arithmetic that produced it.
 *  Both are `null` when the naming organisation holds no `clients_per_year` at all. */
export interface NamedByRow extends LinkRow {
  beneficiaries_computed: number | null;
  beneficiaries_title: string | null;
}

export interface OrganisationLinks {
  items: LinkRow[];
  total: number;
  named_by: NamedByRow[];
  proposed: LinkGhost[];
  mix: LinkMix | null;
  years: number[];
  /** A payer's own channel volume (`channel.measure_participants`) and the people its named
   *  providers account for — Sheet C's coverage line. Absent (`null`) is not zero. */
  channel_volume?: ChannelVolume | null;
  people_accounted?: number | null;
  people_title?: string | null;
  people_year?: number | null;
}

export interface NetworkNode {
  id: string;
  kind: 'organisation' | 'regime' | 'residual';
  organisation_id: number | null;
  ref: string | null;
  name: string;
  canton: string | null;
  subsector: string | null;
  regimes: RegimeToken[];
  is_institution: boolean;
  column: 0 | 1 | 2;
  members?: Array<{ id: number; ref: string | null; name: string }>;
}

export interface NetworkEdge {
  id: string;
  from: string;
  to: string;
  role: LinkRole | 'unit';
  basis: LinkBasis | null;
  quantity: number | null;
  qualifier: LinkQualifier | null;
  year: number | null;
  label: string | null;
  method: LinkRow['method'] | null;
  proposed: boolean;
  computed: boolean;
  held_by_rule: boolean;
  title: string;
}

export interface NetworkSummary {
  organisations: number;
  payers: number;
  links: number;
  proposed: number;
  quantified: number;
  latest_year: number | null;
}

export interface NetworkResponse {
  nodes: NetworkNode[];
  edges: NetworkEdge[];
  summary: NetworkSummary;
}

export interface PayerRow {
  key: string;
  kind: 'organisation' | 'regime';
  organisation_id: number | null;
  ref: string | null;
  name: string;
  regime: RegimeToken | null;
  organisations_naming: number;
  /** The ids of the organisations that name this payer — what `organisations_naming` counts, and
   *  what `group=regime` unions so a body and a regime-alone edge from the same subject count
   *  once. Distinct, ascending. */
  subject_ids: number[];
  counts_given: number;
  share_min: number | null;
  share_max: number | null;
  participants_sum: number | null;
  places_sum: number | null;
  amount_chf_sum: number | null;
  beneficiaries_computed: number | null;
  beneficiaries_title: string | null;
  year: number | null;
  latest_year: number | null;
  /** With `group=regime`: the BODIES folded into this regime's row. A regime-alone edge names no
   *  body and is therefore not one of them, though its figures are in the row's sums. */
  members?: PayerRow[];
}

export interface LinkPayload {
  subject_id?: number;
  counterpart_id?: number;
  counterpart_ref?: string;
  regime?: RegimeToken;
  residual?: LinkResidual;
  role: LinkRole;
  basis?: LinkBasis;
  quantity?: number | string;
  qualifier?: LinkQualifier;
  year?: number;
  label?: string;
  as_of?: string;
  ended_on?: string;
  method?: 'read' | 'estimated' | 'not_found';
  confidence?: number;
  named_in_quote?: boolean;
  why?: string;
}

// ── the flows aggregate (the Network redesign, docs/R5-NETWORK-DESIGN.md) ─────────────────────
//
// `GET /api/network/flows` answers the operator's three questions in one payload: per PAYER, who
// names it and how much of the channel's own volume the known links account for; per PROVIDER,
// its payer mix; and, across the record, the provider × payer matrix. Every figure below that the
// CRM computed (people accounted, coverage, attributed %) carries a `*_title` naming the
// arithmetic and is shown with NO claim mark — arithmetic makes no claim (P12). Anything absent
// is `null`, never 0: "not collected" and "counted, zero" must never look the same (P11).

/** One `channel.measure_participants` reading on a payer body — the coverage bar's denominator. */
export interface ChannelVolume {
  value: number;
  as_of: string | null;
  /** The year the fact speaks for (`EXTRACT(YEAR FROM as_of)`), or null when undated. */
  year: number | null;
  method: string;
  qualifier: LinkQualifier | null;
  /** `group=regime`: true when some member bodies hold no volume, so the Σ is a floor. */
  partial?: boolean;
}

export interface FlowPayer {
  key: string;
  kind: 'organisation' | 'regime' | 'residual';
  organisation_id: number | null;
  ref: string | null;
  name: string;
  canton: string | null;
  regimes: RegimeToken[];
  is_institution: boolean;
  /** The roles this counterpart holds toward the providers naming it (payer · referrer · funder …). */
  roles: LinkRole[];
  /** Distinct providers (subjects) with a held link to this counterpart. */
  providers: number;
  provider_ids: number[];
  /** Providers whose held rows let the CRM count people for this payer in `year`. */
  providers_counted: number;
  held: number;
  proposed: number;
  estimated: number;
  share_min: number | null;
  share_max: number | null;
  participants_sum: number | null;
  places_sum: number | null;
  amount_chf_sum: number | null;
  /** Σ over providers of (participants_count | participants_pct × clients_per_year), within `year`.
   *  Computed by the CRM; `null` when no provider gives a count. */
  people_accounted: number | null;
  people_title: string | null;
  channel_volume: ChannelVolume | null;
  /** `people_accounted / channel_volume × 100`, rounded; `null` without both. */
  coverage_pct: number | null;
  coverage_title: string | null;
  year: number | null;
  latest_year: number | null;
  members?: FlowPayer[];
}

export interface MixSegment {
  key: string;
  name: string;
  ref: string | null;
  organisation_id: number | null;
  quantity: number;
  qualifier: LinkQualifier | null;
  /** For an `amount_chf` mix: the CRM's own division by the same-year revenue. */
  share_computed: number | null;
  method: LinkRow['method'];
  held_by_rule: boolean;
  link_id: number;
}

export interface FlowProvider {
  key: string;
  organisation_id: number;
  ref: string;
  name: string;
  canton: string | null;
  subsector: string | null;
  regimes: RegimeToken[];
  /** Distinct payer / referrer / funder counterparts this provider names. */
  payers: number;
  held: number;
  proposed: number;
  estimated: number;
  clients_per_year: { value: number; as_of: string | null; qualifier: LinkQualifier | null } | null;
  mix_basis: LinkBasis | null;
  mix_year: number | null;
  /** Σ of the mix's shares (or computed shares), rounded — the CRM's own arithmetic. */
  attributed_pct: number | null;
  segments: MixSegment[];
  latest_year: number | null;
}

/** One link row or one unheld proposal, addressed by (provider, payer) — the matrix reads these. */
export interface FlowCell {
  id: string;
  provider: string;
  payer: string;
  role: LinkRole;
  basis: LinkBasis | null;
  quantity: number | null;
  qualifier: LinkQualifier | null;
  year: number | null;
  label: string | null;
  method: LinkRow['method'] | null;
  proposed: boolean;
  held_by_rule: boolean;
  /** `participants_pct × clients_per_year` for this row, when both exist. */
  people_computed: number | null;
  people_title: string | null;
}

export type FlowFacetDim = 'role' | 'canton' | 'regime' | 'subsector' | 'year';
export type FlowFacets = Record<FlowFacetDim, Record<string, number>>;

export interface FlowsSummary {
  /** Every active organisation on the record — the denominator of "providers, of N". */
  organisations_total: number;
  payers: number;
  payer_bodies: number;
  payer_regimes: number;
  payer_residuals: number;
  providers: number;
  links: number;
  held: number;
  proposed: number;
  quantified: number;
  with_share: number;
  /** Σ over payers of `people_accounted`; `null` when nobody gives a count. */
  attributed_people: number | null;
  attributed_providers: number;
  /** Payers (bodies or regime groups) holding a `channel.measure_participants` reading. */
  channel_volumes: number;
  latest_year: number | null;
}

export interface FlowsResponse {
  payers: FlowPayer[];
  providers: FlowProvider[];
  cells: FlowCell[];
  facets: FlowFacets;
  summary: FlowsSummary;
}
